/**
 * GSAP Animations Helper
 * 
 * This file contains reusable GSAP animation functions and utilities
 * for the Gourmet House website.
 * 
 * Usage:
 * - Import this file in your sections or add animations directly
 * - Use the helper functions or create custom animations
 */

// Register GSAP plugins (ScrollTrigger is loaded globally)
if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Register SplitText if available (Club GreenSock plugin)
if (typeof gsap !== 'undefined' && typeof SplitText !== 'undefined') {
  gsap.registerPlugin(SplitText);
}


/**
 * Initialize GSAP animations on page load
 */
function initGSAPAnimations() {
  // Example: Fade in elements on scroll
  gsap.utils.toArray('.gsap-fade-in').forEach((element) => {
    gsap.fromTo(
      element,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play none none reverse',
        },
      }
    );
  });

  // Example: Slide in from left
  gsap.utils.toArray('.gsap-slide-left').forEach((element) => {
    gsap.fromTo(
      element,
      { x: -100, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      }
    );
  });

  // Example: Slide in from right
  gsap.utils.toArray('.gsap-slide-right').forEach((element) => {
    gsap.fromTo(
      element,
      { x: 100, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      }
    );
  });

  // Example: Scale up animation
  gsap.utils.toArray('.gsap-scale-up').forEach((element) => {
    gsap.fromTo(
      element,
      { scale: 0.8, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 0.8,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: element,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      }
    );
  });

  // Example: Stagger animation for lists
  gsap.utils.toArray('.gsap-stagger').forEach((container) => {
    const items = container.querySelectorAll('.gsap-stagger-item');
    if (items.length > 0) {
      gsap.fromTo(
        items,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: container,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }
  });

  // Hero title split animation (line + letter split)
  gsap.utils.toArray('.gsap-hero-title-split').forEach((element) => {
    animateHeroTitleSplit(element);
  });

  // Hero featured image mask animation
  gsap.utils.toArray('.gsap-hero-image-mask').forEach((element) => {
    animateHeroImageMask(element);
  });
}

/**
 * Animate menu drawer open with mask and stagger effects
 * @param {HTMLElement} drawer - The drawer element
 * @param {boolean} isOpen - Whether to open or close
 */
function animateMenuDrawer(drawer, isOpen) {
  if (!drawer || typeof gsap === 'undefined') return;

  // Kill any existing animation on this drawer to prevent conflicts
  if (drawer._menuDrawerTimeline) {
    drawer._menuDrawerTimeline.kill();
    drawer._menuDrawerTimeline = null;
  }

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (isOpen) {
    // Get top-level menu items only - target ONLY the li elements, not inner a/summary tags
    const mainMenu = drawer.querySelector('.menu-drawer__menu.has-submenu');
    const itemsToAnimate = mainMenu 
      ? mainMenu.querySelectorAll(':scope > li')
      : drawer.querySelectorAll('.menu-drawer__menu > li');
    
    // Get social links - animate as one group (desktop version in social container)
    const socialLinks = drawer.querySelector('.menu-drawer__social-container .list-social.list-unstyled') || drawer.querySelector('.list-social.list-unstyled');
    
    // Get featured image - animate with opposite mask direction
    const featuredImage = drawer.querySelector('.nav-featured-items .featured-image.active') || drawer.querySelector('.nav-featured-items .featured img');
    
    // Kill any existing animations on items first
    itemsToAnimate.forEach(item => {
      gsap.killTweensOf(item);
    });
    if (socialLinks) {
      gsap.killTweensOf(socialLinks);
    }
    if (featuredImage) {
      gsap.killTweensOf(featuredImage);
    }
    gsap.killTweensOf(drawer);
    
    // Clear GSAP properties
    gsap.set(drawer, { clearProps: 'clipPath' });
    gsap.set(itemsToAnimate, { clearProps: 'all' });
    if (socialLinks) {
      gsap.set(socialLinks, { clearProps: 'all' });
    }
    if (featuredImage) {
      gsap.set(featuredImage, { clearProps: 'clipPath' });
    }
    
    // Force set initial states immediately (not in animation)
    // Use immediateRender and force3D to ensure clean state
    gsap.set(drawer, {
      clipPath: 'inset(0% 0% 100% 0%)',
      immediateRender: true,
    });
    if (featuredImage) {
      gsap.set(featuredImage, {
        clipPath: 'inset(100% 0% 0% 0%)',
        immediateRender: true,
      });
    }
    gsap.set(itemsToAnimate, {
      opacity: 0,
      y: 40,
      immediateRender: true,
      force3D: false,
    });
    if (socialLinks) {
      gsap.set(socialLinks, {
        opacity: 0,
        y: 40,
        immediateRender: true,
        force3D: false,
      });
    }

    // Create timeline for coordinated animation
    const tl = gsap.timeline({});
    
    // Store timeline reference on drawer element
    drawer._menuDrawerTimeline = tl;

    // Animate the drawer container with clip-path mask from top to bottom
    // Using inset() to create a reveal effect from top to bottom
    // At the same time, animate the image with opposite mask direction (bottom to top)
    tl.to(
      drawer,
      {
        clipPath: 'inset(0% 0% 0% 0%)', // End: fully visible (no mask)
        duration: 0.6,
        ease: CustomEase.create("custom", "M0,0 C0.77,0 0.175,1 1,1 "),
      }
    );
    
    // Stagger animate menu items (translate up from bottom + fade) 
    // Start animation slightly after mask begins for better visual effect
    tl.to(
      itemsToAnimate,
      {
        opacity: 1,
        y: 0,
        duration: 1,
        stagger: 0.05,
        ease: 'expo.out',
        force3D: false,
      },
      '-=0.35'
    );

    // Animate image with opposite mask direction (bottom to top) - happens at same time
    if (featuredImage) {
      tl.to(
        featuredImage,
        {
          clipPath: 'inset(0% 0% 0% 0%)',
          duration: 1,
          ease: CustomEase.create("custom", "M0,0 C0.77,0 0.175,1 1,1 "),
        },
        '-=1.35'
      );
    }

    // Animate social links in as one group after menu items
    if (socialLinks) {
      tl.to(
        socialLinks,
        {
          opacity: 1,
          y: 0, 
          duration: 1,
          ease: 'expo.out',
          force3D: false,
        },
        '-=0.8'
      );
    }

    // If reduced motion, skip animations
    if (prefersReducedMotion) {
      gsap.set(drawer, { clearProps: 'clipPath' });
      if (featuredImage) {
        gsap.set(featuredImage, { clearProps: 'clipPath' });
      }
      gsap.set(itemsToAnimate, { opacity: 1, y: 0 });
      if (socialLinks) {
        gsap.set(socialLinks, { opacity: 1, y: 0 });
      }
      drawer._menuDrawerTimeline = null;
      return null;
    }
    
    // Return timeline for external reference
    return tl;
  } else {
    // Close animation - reverse the effects
    // Get top-level menu items only - target ONLY the li elements, not inner a/summary tags
    const mainMenu = drawer.querySelector('.menu-drawer__menu.has-submenu');
    const itemsToAnimate = mainMenu 
      ? mainMenu.querySelectorAll(':scope > li')
      : drawer.querySelectorAll('.menu-drawer__menu > li');
    
    // Get social links (desktop version in social container)
    const socialLinks = drawer.querySelector('.menu-drawer__social-container .list-social.list-unstyled') || drawer.querySelector('.list-social.list-unstyled');
    
    // Get featured image
    const featuredImage = drawer.querySelector('.nav-featured-items .featured-image.active') || drawer.querySelector('.nav-featured-items .featured img');
    
    // Kill any existing animations on items first
    itemsToAnimate.forEach(item => {
      gsap.killTweensOf(item);
    });
    if (socialLinks) {
      gsap.killTweensOf(socialLinks);
    }
    if (featuredImage) {
      gsap.killTweensOf(featuredImage);
    }
    gsap.killTweensOf(drawer);
    
    // Clear GSAP properties
    gsap.set(drawer, { clearProps: 'clipPath' });
    gsap.set(itemsToAnimate, { clearProps: 'all' });
    if (socialLinks) {
      gsap.set(socialLinks, { clearProps: 'all' });
    }
    if (featuredImage) {
      gsap.set(featuredImage, { clearProps: 'clipPath' });
    }
    
    // Set initial states for close animation (items should be visible at y: 0)
    gsap.set(drawer, {
      clipPath: 'inset(0% 0% 0% 0%)',
      immediateRender: true,
    });
    if (featuredImage) {
      gsap.set(featuredImage, {
        clipPath: 'inset(0% 0% 0% 0%)',
        immediateRender: true,
      });
    }
    gsap.set(itemsToAnimate, {
      opacity: 1,
      y: 0,
      immediateRender: true,
      force3D: false,
    });
    if (socialLinks) {
      gsap.set(socialLinks, {
        opacity: 1,
        y: 0,
        immediateRender: true,
        force3D: false,
      });
    }
    
    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(drawer, { clearProps: 'clipPath' });
        if (featuredImage) {
          gsap.set(featuredImage, { clearProps: 'clipPath' });
        }
        gsap.set(itemsToAnimate, { clearProps: 'all' });
        if (socialLinks) {
          gsap.set(socialLinks, { clearProps: 'all' });
        }
        
        drawer._menuDrawerTimeline = null;
      },
    });
    
    drawer._menuDrawerTimeline = tl;

    // Fade out social links first (as one group)
    if (socialLinks) {
      tl.fromTo(
        socialLinks,
        {
          opacity: 1,
          y: 0, 
        },
        {
          opacity: 0,
          y: -40,
          duration: 0.6,
          ease: 'power2.in',
        }
      );
    }

    // Fade out and translate UP menu items (reverse stagger)
    // Text should move up when closing
    tl.fromTo(
      itemsToAnimate,
      {
        opacity: 1,
        y: 0,
      },
      {
        opacity: 0,
        y: -40, 
        duration: 0.6,
        stagger: 0.02,
        ease: 'power2.in',
      },
      socialLinks ? '-=0.55' : 0
    );

    // Mask out from bottom to top (reverse the reveal)
    // Drawer masks out to bottom, image masks out to top (opposite directions)
    tl.fromTo(
      drawer,
      {
        clipPath: 'inset(0% 0% 0% 0%)',
      },
      {
        clipPath: 'inset(0% 0% 100% 0%)', 
        duration: 0.85,
        ease: CustomEase.create("custom", "M0,0 C0.77,0 0.175,1 1,1 "),
      },
      '-=0.55'
    );
    
    // Animate image with opposite mask direction (top to bottom) - happens at same time
    if (featuredImage) {
      tl.fromTo(
        featuredImage,
        {
          clipPath: 'inset(0% 0% 0% 0%)',
        },
        {
          clipPath: 'inset(0% 0% 100% 0%)',
          duration: 0.85,
          ease: CustomEase.create("custom", "M0,0 C0.77,0 0.175,1 1,1 "),
        },
        '-=0.85' 
      );
    }
    
    return tl;
  }
}

/**
 * Animate search modal open/close with clip-path mask (same as menu drawer)
 * @param {HTMLElement} searchModal - The search modal element
 * @param {boolean} isOpen - Whether to open or close
 */
function animateSearchModal(searchModal, isOpen) {
  if (!searchModal || typeof gsap === 'undefined') return;

  // Kill any existing animation on this modal to prevent conflicts
  if (searchModal._searchModalTimeline) {
    searchModal._searchModalTimeline.kill();
    searchModal._searchModalTimeline = null;
  }

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    return; // Skip animation for reduced motion
  }

  if (isOpen) {
    // Get search input and form elements to animate
    const searchInput = searchModal.querySelector('.search__input, .field__input');
    const searchForm = searchModal.querySelector('.search-modal__form');
    const searchElements = searchInput ? [searchInput] : [];
    if (searchForm) {
      searchElements.push(searchForm);
    }
    
    // Kill any existing animations
    gsap.killTweensOf(searchModal);
    if (searchElements.length > 0) {
      gsap.killTweensOf(searchElements);
    }
    
    // Clear GSAP properties
    gsap.set(searchModal, { clearProps: 'clipPath' });
    if (searchElements.length > 0) {
      gsap.set(searchElements, { clearProps: 'all' });
    }
    
    // Force set initial state immediately (masked from top - opens downward)
    gsap.set(searchModal, {
      clipPath: 'inset(0% 0% 100% 0%)',
      immediateRender: true,
    });
    
    // Set initial state for search elements (fade in + translate up)
    if (searchElements.length > 0) {
      gsap.set(searchElements, {
        opacity: 0,
        y: 40,
        immediateRender: true,
        force3D: false,
      });
    }

    // Create timeline for animation
    const tl = gsap.timeline({});
    
    // Store timeline reference on modal element
    searchModal._searchModalTimeline = tl;

    // Animate the modal container with clip-path mask from top to bottom (opens downward)
    tl.to(
      searchModal,
      {
        clipPath: 'inset(0% 0% 0% 0%)', // End: fully visible (no mask)
        duration: 0.6,
        ease: CustomEase ? CustomEase.create("custom", "M0,0 C0.77,0 0.175,1 1,1 ") : 'power2.out',
      }
    );
    
    // Animate search input/form (translate up from bottom + fade) 
    // Start animation slightly after mask begins for better visual effect
    if (searchElements.length > 0) {
      tl.to(
        searchElements,
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.05,
          ease: 'expo.out',
          force3D: false,
        },
        '-=0.35'
      );
    }
    
    return tl;
  } else {
    // Get search input and form elements to animate
    const searchInput = searchModal.querySelector('.search__input, .field__input');
    const searchForm = searchModal.querySelector('.search-modal__form');
    const searchElements = searchInput ? [searchInput] : [];
    if (searchForm) {
      searchElements.push(searchForm);
    }
    
    // Close animation - mask out from bottom to top (closes upward)
    const tl = gsap.timeline({});
    searchModal._searchModalTimeline = tl;
    
    // Animate search elements out first (fade out + translate down)
    if (searchElements.length > 0) {
      tl.to(
        searchElements,
        {
          opacity: 0,
          y: 40,
          duration: 0.5,
          stagger: 0.03,
          ease: 'power2.in',
          force3D: false,
        },
        0
      );
    }
    
    // Then mask out the modal
    tl.fromTo(
      searchModal,
      {
        clipPath: 'inset(0% 0% 0% 0%)',
      },
      {
        clipPath: 'inset(0% 0% 100% 0%)', 
        duration: 0.85,
        ease: CustomEase ? CustomEase.create("custom", "M0,0 C0.77,0 0.175,1 1,1 ") : 'power2.in',
      },
      searchElements.length > 0 ? '-=0.55' : 0
    );
    
    return tl;
  }
}

/**
 * Animate cart drawer open/close
 * @param {HTMLElement} cartDrawer - The cart drawer element
 * @param {boolean} isOpen - Whether to open or close
 */
function animateCartDrawer(cartDrawer, isOpen) {
  if (!cartDrawer || typeof gsap === 'undefined') return;

  if (isOpen) {
    gsap.fromTo(
      cartDrawer,
      { x: '100%' },
      {
        x: '0%',
        duration: 0.4,
        ease: 'power2.out',
      }
    );
  } else {
    gsap.to(cartDrawer, {
      x: '100%',
      duration: 0.4,
      ease: 'power2.in',
    });
  }
}

/**
 * Parallax scroll effect
 * @deprecated Use ScrollSmoother's data-speed attribute instead for better performance and integration
 * @param {HTMLElement|string} element - Element or selector
 * @param {number} speed - Parallax speed (0-1)
 * 
 * Example with ScrollSmoother:
 * <div data-speed="0.5">Moves slower (parallax down effect)</div>
 * <div data-speed="1.2">Moves faster (parallax up effect)</div>
 */
function initParallax(element, speed = 0.5) {
  // Check if ScrollSmoother is available - if so, recommend using data-speed instead
  if (typeof ScrollSmoother !== 'undefined' && window.scrollSmoother) {
    console.warn('initParallax: Consider using ScrollSmoother\'s data-speed attribute instead for better performance.');
  }
  
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  const el = typeof element === 'string' ? document.querySelector(element) : element;
  if (!el) return;

  gsap.to(el, {
    yPercent: -50 * speed,
    ease: 'none',
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
      scrub: true,
    },
  });
}

/**
 * Text reveal animation (split text effect)
 * @param {HTMLElement|string} element - Element or selector
 */
function animateTextReveal(element) {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  const el = typeof element === 'string' ? document.querySelector(element) : element;
  if (!el) return;

  const text = el.textContent;
  const words = text.split(' ');
  el.innerHTML = words.map((word) => `<span style="display: inline-block;">${word}</span>`).join(' ');

  const wordSpans = el.querySelectorAll('span');

  gsap.fromTo(
    wordSpans,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.5,
      stagger: 0.05,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    }
  );
}

/**
 * Hero title split animation (line split + letter split)
 * Uses GSAP SplitText plugin for clean text splitting
 * Line split: fade and translate up from bottom
 * Letter split: fade in from bottom to top
 * @param {HTMLElement|string} element - Element or selector
 */
function animateHeroTitleSplit(element) {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  const el = typeof element === 'string' ? document.querySelector(element) : element;
  if (!el || el._heroTitleAnimated) return; // Prevent double animation

  // Check if SplitText is available
  if (typeof SplitText === 'undefined') {
    console.warn('SplitText plugin not loaded. Please include GSAP SplitText plugin.');
    return;
  }

  // Use SplitText to split by lines and characters
  const split = new SplitText(el, {
    type: 'lines,chars,words', // Split into lines and characters
    linesClass: 'hero-title-line',
    charsClass: 'hero-title-letter',
    wordsClass: 'hero-title-word',
  });

  const lineDivs = split.lines;
  const letterSpans = split.chars;
  
  if (!lineDivs || lineDivs.length === 0 || !letterSpans || letterSpans.length === 0) {
    split.revert(); // Clean up if split failed
    return;
  }
  
  // Make parent element visible now that split is ready
  gsap.set(el, { opacity: 1 });
  el.classList.add('_gsap-ready');
  
  // Set initial states - lines start 40px down, letters start offset from bottom
  gsap.set(lineDivs, {
    opacity: 0,
    y: 10,
  });
  
  gsap.set(letterSpans, {
    opacity: 0,
    y: 6, // Letters start 6px below, will animate up to 0
  });
  
  // Check if we're on /collections/all - optimize ScrollTrigger config
  const isAllCollectionsPage = window.location.pathname === '/collections/all' || window.location.pathname === '/collections/all/';
  const productGridContainer = document.getElementById('ProductGridContainer');
  const isCollectionGroupTitle = productGridContainer && productGridContainer.contains(el);
  
  // Create timeline with optimized ScrollTrigger config for collection groups
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: el,
      start: 'top 80%',
      once: true, // Only animate once when first seen
      // Optimize for /collections/all to prevent scroll jumping
      ...(isAllCollectionsPage && isCollectionGroupTitle ? {
        invalidateOnRefresh: false, // Don't recalculate on refresh
        refreshPriority: -1, // Lower priority to batch with other updates
        fastScrollEnd: true, // Optimize for fast scrolling
      } : {})
    },
  });
  
  // Animate first line and its letters
  const firstLine = lineDivs[0];
  const firstLineLetters = firstLine ? firstLine.querySelectorAll('.hero-title-letter') : [];
  
  if (firstLine) {
    // First line moves up with airy, lofty easing
    tl.to(firstLine, {
      opacity: 1,
      y: 0,
      duration: 1.1, // Faster duration
      ease: 'power1.out', // Gentler easing for loftier feel
    });
    
    // First line's letters animate up from bottom
    if (firstLineLetters.length > 0) {
      tl.to(
        firstLineLetters,
        {
          opacity: 1,
          y: 0,
          duration: 0.9, // Faster duration
          stagger: 0.02, // Keep the nice stagger spacing
          ease: 'power1.out', // Gentle easing
        },
        '-=0.85' // Start letters almost immediately with line
      );
    }
  }
  
  // Animate subsequent lines - each starts when previous line's letters are almost done
  for (let lineIndex = 1; lineIndex < lineDivs.length; lineIndex++) {
    const line = lineDivs[lineIndex];
    const lettersInLine = line.querySelectorAll('.hero-title-letter');
    
    if (line) {
      // Calculate when previous line's letters are almost finished
      // Previous line's letters duration: 0.9s + (letterCount * 0.02s stagger)
      const prevLine = lineDivs[lineIndex - 1];
      const prevLineLetters = prevLine ? prevLine.querySelectorAll('.hero-title-letter') : [];
      const prevLetterCount = prevLineLetters.length;
      const prevLineLetterDuration = 0.9 + (prevLetterCount * 0.02);
      const startOffset = prevLineLetterDuration - 0.3; // Tighter breathing room
      
      // This line moves up
      tl.to(line, {
        opacity: 1,
        y: 0,
        duration: 1.1, // Faster duration
        ease: 'power1.out', // Gentle easing for loftier feel
      }, `-=${startOffset}`); // Start when previous line's letters are almost done
      
      // This line's letters animate up from bottom
      if (lettersInLine.length > 0) {
        tl.to(
          lettersInLine,
          {
            opacity: 1,
            y: 0,
            duration: 0.9, // Faster duration
            stagger: 0.02, // Keep the nice stagger spacing
            ease: 'power1.out', // Gentle easing
          },
          '-=0.85' // Start letters almost immediately with their line
        );
      }
    }
  }
  
  // Mark as animated and store split reference for cleanup if needed
  el._heroTitleAnimated = true;
  el._splitTextInstance = split; // Store for potential cleanup
}

/**
 * Animate hero featured image with mask reveal (top to bottom)
 * Similar to menu drawer animation
 * Supports both images and videos
 * @param {HTMLElement|string} element - Element or selector
 */
function animateHeroImageMask(element) {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  const el = typeof element === 'string' ? document.querySelector(element) : element;
  if (!el || el._heroImageAnimated) return; // Prevent double animation

  // Look for image, video, or iframe
  const image = el.querySelector('img');
  const video = el.querySelector('video');
  const iframe = el.querySelector('iframe');
  const mediaElement = image || video || iframe;
  
  if (!mediaElement) return;

  // Set initial state - mask from top (media starts fully masked)
  gsap.set(mediaElement, {
    clipPath: 'inset(0% 0% 100% 0%)', // Start: fully masked from bottom
    immediateRender: true,
  });

  // Create timeline with scroll trigger
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: el,
      start: 'top bottom',
      once: true, // Only animate once when first seen
      invalidateOnRefresh: true
    },
  });

  // Animate mask reveal from top to bottom
  tl.to(mediaElement, {
    clipPath: 'inset(0% 0% 0% 0%)', // End: fully visible (no mask)
    duration: 0.85,
    ease: CustomEase.create("custom", "M0,0 C0.77,0 0.175,1 1,1 "), // Custom ease matching menu drawer
  });

  // Mark as animated
  el._heroImageAnimated = true;
}

// Wait for GSAP to be available, then initialize animations
function waitForGSAP() {
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    // GSAP is loaded, initialize animations
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initGSAPAnimations);
    } else {
      initGSAPAnimations();
    }
  } else {
    // GSAP not loaded yet, wait a bit and try again
    setTimeout(waitForGSAP, 50);
  }
}

// Start waiting for GSAP
waitForGSAP();

// Re-initialize animations when Shopify theme editor loads sections
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    // Small delay to ensure DOM is updated
    setTimeout(initGSAPAnimations, 100);
  });
}

// Export functions for use in other scripts
if (typeof window !== 'undefined') {
  window.GSAPAnimations = {
    init: initGSAPAnimations,
    animateMenuDrawer,
    animateSearchModal,
    animateCartDrawer,
    initParallax,
    animateTextReveal,
    animateHeroTitleSplit,
    animateHeroImageMask,
  };
}

